import styled from '@emotion/styled';
import {
  duration,
  easing,
  layout,
  palette,
  radius,
  shadow,
  space,
  spacing,
  typography,
} from '../../../../style-utils/theme';
import { touch, up } from '../../../../style-utils/media-queries';

/**
 * THE VISITOR PASSES REGISTER'S OWN PIECES (resident-portal Plan 3, RP-3.7).
 *
 * The table is the shared engine (components/table/list-table); the building
 * picker is the People screen's TypeFilterSelect; the Live / Ended choice is
 * SegmentedControl. What is here is only what the register adds: the toolbar
 * that holds those two, the building switch, the row name that opens a pass,
 * and the empty state.
 */

export const Screen = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
  min-width: 0;
`;

export const Lead = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
`;

/* Building and Live / Ended. Wraps on a phone rather than squeezing. */
export const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: ${spacing.sm};
  min-width: 0;
`;

export const ToolbarField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  min-width: 0;
  flex: 1 1 ${space(30)};

  ${up('md')} {
    flex: 0 1 ${space(40)};
  }
`;

export const ToolbarLabel = styled.label`
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.inkSoft};
`;

/* ── The building switch ─────────────────────────────────────────────────── */

export const SwitchCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  padding: ${spacing.sm} ${spacing.md};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  background: ${palette.surface};
  box-shadow: ${shadow.xs};
`;

/* The whole row is the label, so the text is part of the target; 44px on
   touch without changing the look under a mouse. */
export const SwitchLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.xs};
  align-self: flex-start;
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
  cursor: pointer;

  ${touch} {
    min-height: ${layout.touchTarget};
  }

  input {
    width: ${spacing.md};
    height: ${spacing.md};
    margin: 0;
    accent-color: ${palette.lagoon};
    cursor: pointer;
  }

  input:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  input:disabled {
    cursor: default;
  }
`;

export const SwitchHint = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
`;

/* ── Rows ────────────────────────────────────────────────────────────────── */

/* The visitor's name opens the pass. Reads as the name, underlines on hover. */
export const NameButton = styled.button`
  -webkit-appearance: none;
  appearance: none;
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  /* 24px under a mouse — the WCAG 2.5.8 floor the audit scanner holds every
     control to — and the 44px touch floor below. The text does not move. */
  min-height: ${space(3)};
  margin: 0;
  padding: 0;
  border: none;
  border-radius: ${radius.xs};
  background: transparent;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
  text-align: left;
  overflow-wrap: anywhere;
  cursor: pointer;
  transition: color ${duration.fast} ${easing.standard};

  &:hover {
    color: ${palette.lagoon};
    text-decoration: underline;
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  ${touch} {
    min-height: ${layout.touchTarget};
  }
`;

export const Sub = styled.span`
  display: block;
  margin-top: ${spacing.half};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  color: ${palette.slate};
  overflow-wrap: anywhere;
`;

export const RowActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: ${spacing.xs};
`;

export const Empty = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  padding: ${spacing.lg} ${spacing.md};
  border: 1px dashed ${palette.lineStrong};
  border-radius: ${radius.md};
  background: ${palette.surface};
  text-align: center;
  font-family: ${typography.ui};
`;

export const EmptyTitle = styled.p`
  margin: 0;
  font-size: ${typography.size.base}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
`;

export const EmptyText = styled.p`
  margin: 0;
  font-size: ${typography.size.sm}px;
  color: ${palette.slate};
`;

export const Footer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.xs} ${spacing.sm};
  min-height: ${layout.touchTarget};
`;

export const FooterNote = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  color: ${palette.slate};

  &:focus {
    outline: none;
  }
`;
